import { t } from '@lingui/macro';
import React, {
  useEffect,
  useRef,
  useState,
  FunctionComponent,
  SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';

import {
  announcePolite,
  getListOptionAnnounceText,
  offscreenLiveRegionStyle,
} from '@helpers/screenReaderAnnounce';

import {
  DropdownKeys,
  DropdownVariants,
  ListDropdownTransitions,
  ListOpenDirections,
  ListOptionNullOmitted,
  ListOptionValueNoNull,
  MultiselectDropdownProps
} from '@components/library/dropdown';
import BaseDropdown from '@components/library/dropdown/base/BaseDropdown';
import { dropdownKeydownHelper } from '@components/library/dropdown/dropdown_helper';
import { MultiselectAnchorElement } from '@components/library/dropdown/multiselect/MultiselectAnchorElement';
import { MultiselectOptions } from '@components/library/dropdown/multiselect/MultiselectOptions';
import { ListDropdownChildNode } from '@components/common/search_result_bolding/main';

import styleClassNames from './multiselect-dropdown.module.scss';

let multiselectListItemPrefixCounter = 0;

export const MultiselectDropdown: FunctionComponent<MultiselectDropdownProps> = (
  props
) => {
  const {
    anchorElement,
    anchorElementLabel,
    cfDropdown = false,
    disabled,
    dropdownTransition = ListDropdownTransitions.FadeIn,
    dropdownTransitionTime = 300,
    error,
    onChange,
    onOpen,
    openDirection = ListOpenDirections.Down,
    options,
    placeholder = t`Select values...`,
    searchable = false,
    toggleDropdown: toggleDropdownProp,
    values: optionValues, // renaming for readability
    virtualizeOptions = true,
    ...baseDropdownProps
  } = props;
  const listItemIdPrefixRef = useRef<string | null>(null);
  if (listItemIdPrefixRef.current === null) {
    listItemIdPrefixRef.current = `ms-li-${++multiselectListItemPrefixCounter}`;
  }
  const listItemIdPrefix = listItemIdPrefixRef.current;
  const isMounted = useRef(true);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // tracks open/closed status of dropdown
  const [searchInput, setSearchInput] = useState('');
  const [arrowedItem, setArrowedItem] = useState<ListOptionValueNoNull | undefined>();
  const [selectedPillValue, setSelectedPillValue] = useState<ListOptionValueNoNull | null>(null);
  const [storedTimeouts, setStoredTimeouts] = useState<number[]>([]);

  const cancelStoredTimeouts = (): void => {
    storedTimeouts.forEach((storedTimeout) => clearTimeout(storedTimeout));
  };

  const cleanup = (): void => {
    cancelStoredTimeouts(); // cancel all outstanding timeouts when unmounting
    isMounted.current = false;
  };

  // build selectedOptions object from undefined || ListOption[] || ListOptionValueNoNull[]
  // allows consumer to pass in either an array of list options or list option values for values prop
  let selectedOptions: ListOptionNullOmitted[] = [];

  if (optionValues?.length) {
    if ((optionValues as ListOptionNullOmitted[])[0]?.label !== undefined) {
      selectedOptions = optionValues as ListOptionNullOmitted[];
    } else if (options) {
      // eslint-disable-next-line array-callback-return
      (optionValues as ListOptionValueNoNull[]).map((selectedOptionValue) => { // eslint-disable-line consistent-return
        const foundOption = options.find((option) => option.value === selectedOptionValue);
        if (foundOption) {
          selectedOptions.push(foundOption);
        }
      });
    }
  }

  const searchedOptions = options?.filter((option) => {
    const toSearch = cfDropdown ? (option.searchValue || option.value || option.label) : (option.searchValue || option.label);
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    return toSearch?.toString().toLowerCase().includes(searchInput?.toLowerCase());
  });

  const dropdownChildren = (): ListOptionNullOmitted[] => {
    if (cfDropdown) {
      searchedOptions?.forEach((elem) => {
        elem.label = ListDropdownChildNode({ searchResult: elem.value as string || elem.searchValue || '', searchInput });
      });
      return searchedOptions;
    }

    return searchedOptions;
  };

  const handleOnChange = (selectedOptionValue: ListOptionValueNoNull | undefined, e?: SyntheticEvent): void => {
    e?.stopPropagation(); // eslint-disable-line no-unused-expressions
    e?.preventDefault(); // eslint-disable-line no-unused-expressions
    let isAdded = false;

    const updatedSelectedOptionValues = [...selectedOptions.map((selectedOption) => selectedOption.value)];
    const newOptionIndex = selectedOptions.findIndex((selectedOption) => (
      selectedOption.value === selectedOptionValue
      || String(selectedOption.value) === String(selectedOptionValue)
    ));
    if (newOptionIndex > -1) {
      const removedOption = selectedOptions[newOptionIndex];
      const removedLabel = getListOptionAnnounceText(removedOption);
      if (removedLabel) {
        // Defer past the pill close button; assertive + body portal so NVDA/JAWS reliably pick this up.
        window.setTimeout(() => {
          if (isMounted.current) {
            announcePolite(liveRegionRef, t`Removed ${removedLabel}`, { assertive: true });
          }
        }, 100);
      }
      updatedSelectedOptionValues.splice(newOptionIndex, 1);
      if (searchable) setSearchInput('');
    } else if (selectedOptionValue !== undefined) {
      isAdded = true;
      updatedSelectedOptionValues.push(selectedOptionValue);
      if (searchable) setSearchInput('');
    }

    if (onChange) {
      onChange(updatedSelectedOptionValues, isAdded, selectedOptionValue);
    }
  };

  const handleArrowingUpAndDown = (key: DropdownKeys, e: KeyboardEvent): void => {
    e.stopPropagation();
    e.preventDefault();

    const targetedIndex = searchedOptions?.findIndex((option) => option.value === arrowedItem);
    const newIndex = dropdownKeydownHelper(key, searchedOptions?.length, targetedIndex);

    if (typeof newIndex === 'number') {
      // ternary here is important (instead of checking value to be trutyh)
      // to account for value === 0 and value === undefined (which are allowed)
      setArrowedItem(searchedOptions?.[newIndex] ? searchedOptions?.[newIndex].value : undefined);
    } else if (newIndex) {
      handleOnChange(arrowedItem);
    }
  };

  const handleArrowingLeftAndRight = (key: DropdownKeys): void => {
    const selectedPillIndex = selectedOptions.findIndex((option) => option.value === selectedPillValue);
    const newPillIndex = dropdownKeydownHelper(key, selectedOptions?.length, selectedPillIndex);

    if (typeof newPillIndex === 'number') {
      setSelectedPillValue(selectedOptions[newPillIndex].value);
    }
  };

  const handleKeydown = (key: DropdownKeys, e: KeyboardEvent): void => {
    switch (key) {
      case DropdownKeys.ArrowDown:
      case DropdownKeys.ArrowUp:
      case DropdownKeys.Enter: {
        handleArrowingUpAndDown(key, e);
        break;
      }
      case DropdownKeys.Spacebar:
      case DropdownKeys.Space: {
        // if searchable, we don't want to intercept the spacebar keydown here (avoid interfering with typing in the search input)
        if (!searchable) handleArrowingUpAndDown(key, e);
        break;
      }
      case DropdownKeys.ArrowLeft:
      case DropdownKeys.ArrowRight: {
        if (searchable && searchInput.length) break; // break and avoid arrowing the pills if user is typing in the text input
        if (!selectedOptions?.length) {
          // if there are no pills selected, arrowing left and right behave like up and down arrow
          handleArrowingUpAndDown(key, e);
        } else {
          handleArrowingLeftAndRight(key);
        }
        break;
      }
      case DropdownKeys.Backspace: {
        if (selectedPillValue) {
          const selectedPillIndex = selectedOptions?.findIndex((option) => option.value === selectedPillValue);
          const newSelectedPillValue = selectedPillIndex === 0
            ? selectedOptions?.[1]?.value || null // null if you're removing the only pill
            : selectedOptions[selectedPillIndex - 1].value;
          handleOnChange(selectedPillValue);
          setSelectedPillValue(newSelectedPillValue || null);
        } else if (searchable && !searchInput.length && selectedOptions.length) {
          // delete last pill if user hits 'backspace' when search input is empty
          const lastPill = selectedOptions[selectedOptions.length - 1];
          handleOnChange(lastPill.value);
        }
        break;
      }
      case DropdownKeys.End:
        e.stopPropagation();
        e.preventDefault();
        setArrowedItem(searchedOptions?.[searchedOptions.length - 1]
          ? searchedOptions?.[searchedOptions.length - 1].value
          : undefined);
        break;
      case DropdownKeys.Home:
        e.stopPropagation();
        e.preventDefault();
        setArrowedItem(searchedOptions?.[0] ? searchedOptions?.[0].value : undefined);
        break;
      default:
        break;
    }
  };

  const defaultAnchorElement = (
    <MultiselectAnchorElement
      anchorElementLabel={anchorElementLabel}
      cfDropdown={cfDropdown}
      disabled={disabled}
      error={error}
      isDropdownOpen={isDropdownOpen}
      onChange={handleOnChange}
      placeholder={placeholder}
      searchInput={searchInput}
      searchable={searchable}
      selectedOptions={selectedOptions}
      selectedPillValue={selectedPillValue}
      setSearchInput={setSearchInput}
      setSelectedPillValue={setSelectedPillValue}
    />
  );

  const getOptions = (
    <MultiselectOptions
      arrowedItem={arrowedItem}
      handleOnChange={handleOnChange}
      listItemIdPrefix={listItemIdPrefix}
      options={options}
      searchable={searchable}
      searchedOptions={dropdownChildren()}
      selectedOptions={selectedOptions}
      styleClassNames={styleClassNames}
      virtualizeOptions={virtualizeOptions}
    />
  );

  useEffect(() => {
    if (isDropdownOpen) {
      if (onOpen) {
        onOpen();
      }

      // Per WAI-ARIA APG combobox/listbox pattern, point aria-activedescendant at a specific
      // option as soon as the listbox is shown so screen readers announce only the focused
      // option rather than the entire list at once.
      const visibleOptions = searchable && searchedOptions?.length ? searchedOptions : options;
      if (visibleOptions?.length) {
        const firstSelectedInVisible = selectedOptions.find((selected) => (
          visibleOptions.some((option) => option.value === selected.value)
        ));
        const initialArrowedValue = firstSelectedInVisible
          ? firstSelectedInVisible.value
          : visibleOptions[0].value;
        setArrowedItem(initialArrowedValue);
      }
    }

    if (!isDropdownOpen) {
      setArrowedItem(undefined);
      setSelectedPillValue(null);

      if (searchable) {
        const newTimeout = window.setTimeout(() => {
          if (isMounted.current) setSearchInput('');
        }, dropdownTransitionTime + 50); // setTimeout ensures search input isn't cleared until after dropdown closes (otherwise dropdown will jump back to unsearched state)
        setStoredTimeouts((timeouts) => ([...timeouts, newTimeout]));
      }
    }
  }, [isDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => cleanup, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseProps = {
    ...baseDropdownProps,
    anchorElement: anchorElement || defaultAnchorElement,
    arrowedItem,
    children: getOptions,
    listItemIdPrefix,
    disabled,
    dropdownTransition,
    dropdownTransitionTime,
    error,
    keydownCallback: handleKeydown,
    openDirection,
    setIsDropdownOpen,
    expandOnSpacebarKeypress: !searchable,
    searchable,
    styleClassNames,
    toggleDropdown: toggleDropdownProp,
    useUlContainer: true,
    variantName: DropdownVariants.Multiselect,
    virtualizeOptions,
  };

  const liveRegionNode = (
    <div
      ref={liveRegionRef}
      aria-atomic="true"
      aria-live="polite"
      role="status"
      style={offscreenLiveRegionStyle}
    />
  );

  return (
    <>
      {typeof document !== 'undefined'
        ? createPortal(liveRegionNode, document.body)
        : liveRegionNode}
      <BaseDropdown {...baseProps} />
    </>
  );
};
